import {
  AwaitingRegisteredComponentResolver,
  AwaitingComponentResolvers,
  ExportedSingleComponent,
  ResolvedComponent,
  ComponentResolver,
} from 'ca-dom-binding/types';

const componentResolverMap = new Map<string, ComponentResolver>();

const awaitingResolution = new Map<string, AwaitingComponentResolvers>();

function setForName(name: string): AwaitingComponentResolvers {
  const resolver = awaitingResolution.get(name);
  if (resolver !== undefined) {
    return resolver;
  }
  const set = new Set<AwaitingRegisteredComponentResolver>();
  awaitingResolution.set(name, set);
  return set;
}

export async function getRegisteredComponent(
  name: string
): Promise<ExportedSingleComponent | unknown> {
  const resolver = componentResolverMap.get(name);
  if (resolver) {
    const resolvedComponent: ResolvedComponent = await resolver();
    return componentFromResolved(resolvedComponent);
  }
  return addWaitForRegistrationResolver(name);
}

function addWaitForRegistrationResolver(name: string): Promise<unknown> {
  const set: AwaitingComponentResolvers = setForName(name);

  return new Promise((resolve) => {
    const onSuccess = (delayedComponent: ResolvedComponent): void => {
      clearTimeout(loadedTooSlowTimeout);
      resolve(componentFromResolved(delayedComponent));
    };
    const loadedTooSlowTimeout = setTimeout(() => {
      set.delete(onSuccess as AwaitingRegisteredComponentResolver);
      throw new Error(`Failed to load registered component ${name}`);
    }, 5000);
    set.add(onSuccess as AwaitingRegisteredComponentResolver);
  });
}

function componentFromResolved(
  component: ResolvedComponent
): ExportedSingleComponent {
  if ('default' in component) {
    return component.default;
  }
  return component;
}

export async function registerComponent(
  name: string,
  resolver: ComponentResolver
): Promise<void> {
  componentResolverMap.set(name, resolver);
  //  Check to see if there are any awaiting resolvers.
  const set: AwaitingComponentResolvers = setForName(name);
  if (set.size > 0) {
    const resolvedComponent: ResolvedComponent = await resolver();
    //  resolve resolvers
    set.forEach(
      (awaitingRegisteredComponent: AwaitingRegisteredComponentResolver) => {
        awaitingRegisteredComponent(resolvedComponent);
      }
    );
  }
}

export function resolveComponent(component: ExportedSingleComponent) {
  return async () => component;
}
